/**
 * NER-4488 — Wizard de plantillas de combos (4 pasos).
 *
 *   1. Elegí una plantilla       → catálogo `src/data/comboTemplates.js`
 *   2. Completá los espacios     → items fijos / a elección por espacio
 *   3. Precio y presentación     → suma de componentes, descuento, badge, categoría, imagen
 *   4. Revisá y publicá          → preview + resumen de lo que se crea
 *
 * Resultado: un MenuItem `isCombo` normal. Los espacios a elección se
 * materializan como grupos de modificadores (lo único que hoy el cliente puede
 * elegir y que llega a la comanda). Ver `src/utils/comboTemplates.js`.
 *
 * Rutas: `/menu/combos/new` y `/menu/combos/:itemId/edit` (solo combos creados
 * con plantilla; los combos legacy siguen usando el formulario completo).
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Search,
  Sandwich,
  UtensilsCrossed,
  LayoutTemplate,
  Users,
  PartyPopper,
  Sun,
  Coffee,
  Wand2,
  Info,
} from 'lucide-react';
import {
  getMenu,
  createItem,
  updateItem,
  createCategory,
  getModifierGroups,
  createModifierGroup,
  updateModifierGroup,
  toggleItemAvailability,
  getOrgTemplates,
  createOrgTemplate,
  deleteOrgTemplate,
} from '../services/api';
import { storage } from '../utils/storage';
import TemplatePicker from '../components/TemplatePicker';
import {
  ORG_TEMPLATE_KINDS,
  slotLabel,
  buildComboTemplatePayload,
  slotsFromComboTemplatePayload,
  describeOrgTemplate,
} from '../utils/orgTemplates';
import { formatCurrency } from '../utils/formatCurrency';
import { normalizeModifierGroupIds } from '../utils/itemForm';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import HelpIcon from '../components/help/HelpIcon';
import ImageUpload from '../components/ImageUpload';
import AvailabilitySchedule from '../components/AvailabilitySchedule';
import {
  COMBO_TEMPLATES,
  CUSTOM_TEMPLATE_ID,
  NEW_COMBOS_CATEGORY,
  getComboTemplate,
  makeCustomSlot,
} from '../data/comboTemplates';
import {
  buildInitialSlots,
  slotStateFromDefinition,
  indexItems,
  selectableCategories,
  computeComponentsSum,
  suggestPrice,
  impliedDiscountPct,
  validateSlots,
  buildModifierGroupBody,
  buildComboItemPayload,
  optionItemIdsFor,
  hydrateFromComboItem,
  isNewCombosCategory,
  findCombosCategory,
  normalizeText,
} from '../utils/comboTemplates';
import './ComboWizardPage.css';

const TEMPLATE_ICONS = {
  Sandwich,
  UtensilsCrossed,
  LayoutTemplate,
  Users,
  PartyPopper,
  Sun,
  Coffee,
  Wand2,
};

const DISCOUNT_PRESETS = [10, 15, 20];
const STEP_KEYS = ['template', 'slots', 'pricing', 'review'];

function TemplateIcon({ name, size = 22 }) {
  const Icon = TEMPLATE_ICONS[name] || LayoutTemplate;
  return <Icon size={size} aria-hidden="true" />;
}

/** Texto corto "1 principal · bebida a elección" para las cards de plantilla. */
function templateSummary(template, t) {
  if (!template.slots.length) return t('comboWizard.templates.custom.summary');
  return template.slots
    .map((slot) => {
      const label = t(slot.labelKey);
      if (slot.kind === 'fixed') {
        return `${slot.quantity || 1} ${label}`;
      }
      return t('comboWizard.summary.choiceChip', { label, count: slot.max || 1 });
    })
    .join(' · ');
}

function ComboWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const isEdit = Boolean(itemId);
  const { setDynamicLabel } = useBreadcrumb();

  const [step, setStep] = useState(isEdit ? 2 : 1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [categories, setCategories] = useState([]);
  const [modifierGroupsList, setModifierGroupsList] = useState([]);

  const [templateId, setTemplateId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotSearch, setSlotSearch] = useState({});
  const [slotErrors, setSlotErrors] = useState([]);
  const [quickCreate, setQuickCreate] = useState(null);

  const [pricing, setPricing] = useState({ mode: 'pct', pct: 15, price: '' });
  // RES-68: al pasar a "Manual" seleccionamos el valor pre-cargado (ver renderPricing)
  // para que escribir reemplace en vez de insertar — si no, el precio se concatena.
  const priceInputRef = useRef(null);
  const [presentation, setPresentation] = useState({
    name: '',
    description: '',
    badge: '',
    categoryId: '',
    imageUrl: '',
    availability: null,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  // true cuando el usuario editó la descripción a mano: en ese caso no se
  // regenera al cambiar los espacios (QA 2026-09-13: quedaba desactualizada).
  const [descriptionDirty, setDescriptionDirty] = useState(false);

  const [editItem, setEditItem] = useState(null);
  const [legacyCombo, setLegacyCombo] = useState(false);
  const [publish, setPublish] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Plantillas propias de la organización (NER-4488 fase 3)
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const byId = useMemo(() => indexItems(categories), [categories]);
  const pickableCategories = useMemo(() => selectableCategories(categories), [categories]);
  const { sum: componentsSum } = useMemo(() => computeComponentsSum(slots, byId), [slots, byId]);
  const effectivePrice =
    pricing.mode === 'pct' ? suggestPrice(componentsSum, pricing.pct) : Number(pricing.price) || 0;
  const discountPct = impliedDiscountPct(componentsSum, effectivePrice);
  const template = getComboTemplate(templateId);

  // ─── Carga ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const response = await getMenu();
      const activeMenu = (response.menu || [])
        .filter((cat) => cat.status !== 'deleted')
        .map((cat) => ({
          ...cat,
          items: (cat.items || []).filter((item) => item.status !== 'deleted'),
        }));
      setCategories(activeMenu);

      const restaurantId = storage.get('selectedRestaurantId');
      if (restaurantId) {
        const modData = await getModifierGroups(restaurantId);
        setModifierGroupsList(modData.modifierGroups || []);
      }
      return activeMenu;
    } catch (error) {
      console.error('[ComboWizard] Error loading menu:', error);
      setLoadError(t('comboWizard.errors.loadError'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const activeMenu = await loadData();
      if (cancelled || !activeMenu) return;

      const combosCategory = findCombosCategory(activeMenu);
      const defaultCategoryId = combosCategory ? combosCategory.categoryId : NEW_COMBOS_CATEGORY;

      if (isEdit) {
        let found = null;
        for (const cat of activeMenu) {
          const match = (cat.items || []).find((item) => item.itemId === itemId);
          if (match) {
            found = { ...match, categoryId: cat.categoryId };
            break;
          }
        }
        if (!found) {
          setLoadError(t('comboWizard.errors.notFound'));
          return;
        }
        setEditItem(found);
        setDynamicLabel(found.name);
        const hydrated = hydrateFromComboItem(found, activeMenu);
        if (!hydrated) {
          setLegacyCombo(true);
          return;
        }
        setTemplateId(hydrated.templateId);
        setSlots(hydrated.slots);
        setPresentation({
          ...hydrated.presentation,
          categoryId: hydrated.presentation.categoryId || defaultCategoryId,
        });
        // Al editar, la descripción guardada se respeta hasta que el usuario
        // pida regenerarla.
        setDescriptionDirty(Boolean(hydrated.presentation.description));
        setPricing({ mode: 'manual', pct: 0, price: String(hydrated.price) });
        setPublish(found.isAvailable !== false);
      } else {
        setPresentation((prev) => ({ ...prev, categoryId: defaultCategoryId }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // Plantillas de la organización: se comparten entre todos sus restaurantes.
  const loadOrgTemplates = useCallback(async () => {
    try {
      setOrgLoading(true);
      const data = await getOrgTemplates(ORG_TEMPLATE_KINDS.COMBO);
      setOrgTemplates(data.templates || []);
    } catch (error) {
      console.error('[ComboWizard] org templates failed:', error);
      setOrgTemplates([]);
    } finally {
      setOrgLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isEdit) loadOrgTemplates();
  }, [isEdit, loadOrgTemplates]);

  // ─── Paso 1: plantilla ─────────────────────────────────────────────────
  const handleSelectOrgTemplate = (orgTemplateId) => {
    const tpl = orgTemplates.find((item) => item.templateId === orgTemplateId);
    if (!tpl) return;
    const payload = tpl.payload || {};
    setTemplateId(payload.sourceTemplateId || CUSTOM_TEMPLATE_ID);
    setSlots(slotsFromComboTemplatePayload(payload, categories));
    setSlotErrors([]);
    setPricing((prev) => ({ ...prev, mode: 'pct', pct: payload.defaultDiscountPct || 0 }));
    setPresentation((prev) => ({
      ...prev,
      name: prev.name || payload.suggestedName || tpl.name,
      description: prev.description || payload.description || '',
      badge: prev.badge || payload.badge || '',
    }));
    setStep(2);
  };

  const handleDeleteOrgTemplate = async (orgTemplateId) => {
    try {
      await deleteOrgTemplate(orgTemplateId);
      await loadOrgTemplates();
    } catch (error) {
      console.error('[ComboWizard] delete org template failed:', error);
    }
  };

  const handleSelectTemplate = (id) => {
    const tpl = getComboTemplate(id);
    if (!tpl) return;
    setTemplateId(id);
    setSlots(buildInitialSlots(id, categories));
    setSlotErrors([]);
    setPricing((prev) => ({ ...prev, mode: 'pct', pct: tpl.defaultDiscountPct || 0 }));
    setPresentation((prev) => ({
      ...prev,
      name: prev.name || t(`comboWizard.templates.${id}.suggestedName`),
      badge: prev.badge || '',
    }));
    setStep(2);
  };

  // ─── Paso 2: espacios ──────────────────────────────────────────────────
  const updateSlot = (slotId, patch) => {
    setSlots((prev) => prev.map((slot) => (slot.slotId === slotId ? { ...slot, ...patch } : slot)));
    setSlotErrors((prev) => prev.filter((err) => err.slotId !== slotId));
  };

  const toggleSlotItem = (slot, item) => {
    if (slot.kind === 'fixed') {
      updateSlot(slot.slotId, { itemIds: [item.itemId] });
      return;
    }
    const has = slot.itemIds.includes(item.itemId);
    const itemIds = has
      ? slot.itemIds.filter((id) => id !== item.itemId)
      : [...slot.itemIds, item.itemId];
    const extraPrices = { ...slot.extraPrices };
    if (has) delete extraPrices[item.itemId];
    updateSlot(slot.slotId, { itemIds, extraPrices });
  };

  const addSlot = (kind) => {
    const definition = makeCustomSlot(kind, Date.now());
    setSlots((prev) => [...prev, slotStateFromDefinition(definition, categories)]);
  };

  const removeSlot = (slotId) => {
    setSlots((prev) => prev.filter((slot) => slot.slotId !== slotId));
    setSlotErrors((prev) => prev.filter((err) => err.slotId !== slotId));
  };

  const slotItems = (slot) => {
    const search = normalizeText(slotSearch[slot.slotId] || '');
    const source = slot.categoryId
      ? categories.filter((cat) => cat.categoryId === slot.categoryId)
      : categories;
    const items = [];
    for (const cat of source) {
      for (const item of cat.items || []) {
        if (item.isCombo || item.itemId === itemId) continue;
        if (search && !normalizeText(item.name).includes(search)) continue;
        items.push({ ...item, categoryName: cat.name });
      }
    }
    return items;
  };

  const handleQuickCreate = async (slot) => {
    if (!quickCreate || quickCreate.slotId !== slot.slotId) return;
    const name = (quickCreate.name || '').trim();
    const price = parseFloat(quickCreate.price);
    if (!name || Number.isNaN(price) || price < 0 || !slot.categoryId) {
      setQuickCreate({ ...quickCreate, error: t('comboWizard.errors.quickCreateInvalid') });
      return;
    }
    try {
      setQuickCreate({ ...quickCreate, saving: true, error: '' });
      const created = await createItem({
        categoryId: slot.categoryId,
        name,
        price,
        preparationTime: 15,
      });
      const activeMenu = await loadData();
      if (activeMenu && created?.itemId) {
        updateSlot(slot.slotId, {
          itemIds: slot.kind === 'fixed' ? [created.itemId] : [...slot.itemIds, created.itemId],
        });
      }
      setQuickCreate(null);
    } catch (error) {
      console.error('[ComboWizard] quick create failed:', error);
      setQuickCreate({
        ...quickCreate,
        saving: false,
        error: error.response?.data?.error || t('comboWizard.errors.quickCreateError'),
      });
    }
  };

  const goToPricing = () => {
    const errors = validateSlots(slots);
    if (slots.length === 0) {
      setSlotErrors([{ slotId: '__none__', reason: 'noSlots' }]);
      return;
    }
    if (errors.length > 0) {
      setSlotErrors(errors);
      return;
    }
    setSlotErrors([]);
    // Se regenera con los espacios salvo que el usuario la haya editado a mano.
    if (!presentation.description || !descriptionDirty) {
      setPresentation((prev) => ({ ...prev, description: buildDescription() }));
    }
    setStep(3);
  };

  // ─── Paso 3: precio y presentación ─────────────────────────────────────
  const buildDescription = () => {
    const fixedNames = [];
    const choices = [];
    for (const slot of slots) {
      const items = slot.itemIds.map((id) => byId.get(id)).filter(Boolean);
      if (!items.length) continue;
      if (slot.kind === 'fixed') {
        fixedNames.push(
          ...items.map((item) =>
            (slot.quantity || 1) > 1 ? `${slot.quantity} ${item.name}` : item.name
          )
        );
      } else {
        choices.push(
          t('comboWizard.summary.chooseSentence', {
            label: slotLabel(slot, t).toLowerCase(),
            options: items.map((item) => item.name).join(', '),
          })
        );
      }
    }
    const parts = [];
    if (fixedNames.length) {
      parts.push(t('comboWizard.summary.includesSentence', { items: fixedNames.join(', ') }));
    }
    parts.push(...choices);
    return parts.join('. ');
  };

  const mainImageCandidate = useMemo(() => {
    for (const slot of slots) {
      if (slot.kind !== 'fixed') continue;
      const item = slot.itemIds.map((id) => byId.get(id)).find((it) => it?.imageUrl);
      if (item) return item;
    }
    for (const slot of slots) {
      const item = slot.itemIds.map((id) => byId.get(id)).find((it) => it?.imageUrl);
      if (item) return item;
    }
    return null;
  }, [slots, byId]);

  const goToReview = () => {
    const errors = {};
    if (!presentation.name.trim()) errors.name = t('comboWizard.errors.nameRequired');
    // RES-68: el precio manual llega pre-cargado con la suma de componentes; si el
    // admin escribe sin seleccionar el valor previo, el resultado puede concatenarse
    // (ej. "3000" + "2700" → "30002700"). El fix de foco+selección de más abajo evita
    // el caso normal, pero esta validación es la red de seguridad del lado del cliente
    // para cuando igual llega un valor desproporcionado.
    if (!(effectivePrice > 0)) {
      errors.price = t('comboWizard.errors.priceRequired');
    } else if (
      pricing.mode === 'manual' &&
      componentsSum > 0 &&
      effectivePrice > componentsSum * 5
    ) {
      errors.price = t('comboWizard.errors.priceUnreasonable', {
        price: formatCurrency(effectivePrice),
        sum: formatCurrency(componentsSum),
      });
    }
    if (!presentation.categoryId) errors.categoryId = t('comboWizard.errors.categoryRequired');
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setStep(4);
  };

  // ─── Paso 4: guardar ───────────────────────────────────────────────────
  const handleSave = async (shouldPublish) => {
    setSaveError('');
    setSaving(true);
    try {
      const restaurantId = storage.get('selectedRestaurantId');
      let categoryId = presentation.categoryId;
      if (isNewCombosCategory(categoryId)) {
        const createdCategory = await createCategory({
          name: t('comboWizard.defaults.combosCategory'),
          displayOrder: categories.length + 1,
          preparationArea: 'both',
        });
        categoryId = createdCategory.categoryId;
      }

      const groupIdsBySlot = {};
      // RES-32: qué producto representa cada opción del grupo, para el motor de consumo
      const optionItemIdsBySlot = {};
      for (const slot of slots) {
        if (slot.kind !== 'choice') continue;
        const label = slotLabel(slot, t);
        const existing = slot.groupId
          ? modifierGroupsList.find((group) => group.groupId === slot.groupId)
          : null;
        const body = buildModifierGroupBody(slot, label, presentation.name, byId, existing);
        const saved = existing
          ? await updateModifierGroup(restaurantId, existing.groupId, body)
          : await createModifierGroup(restaurantId, body);
        groupIdsBySlot[slot.slotId] = saved?.groupId || existing?.groupId;
        optionItemIdsBySlot[slot.slotId] = optionItemIdsFor(slot, saved, byId);
      }

      const payload = buildComboItemPayload({
        templateId: templateId || CUSTOM_TEMPLATE_ID,
        slots,
        groupIdsBySlot,
        optionItemIdsBySlot,
        pricing: { price: effectivePrice, componentsSum },
        presentation,
        byId,
      });
      payload.categoryId = categoryId;

      if (isEdit && editItem) {
        // Conservar grupos de modificadores agregados a mano fuera del wizard.
        const slotGroupIds = new Set([
          ...Object.values(groupIdsBySlot),
          ...(editItem.comboItems?.slots || []).map((slot) => slot.groupId).filter(Boolean),
        ]);
        const manualGroupIds = normalizeModifierGroupIds(
          Array.isArray(editItem.modifierGroupIds)
            ? editItem.modifierGroupIds
            : editItem.modifierGroups
        ).filter((id) => !slotGroupIds.has(id));
        payload.modifierGroups = [...new Set([...payload.modifierGroups, ...manualGroupIds])];
        if (payload.originalPrice === undefined) payload.originalPrice = 0;
        if (payload.promotionBadge === undefined) payload.promotionBadge = '';
        await updateItem(editItem.itemId, payload);
        if (shouldPublish !== (editItem.isAvailable !== false)) {
          await toggleItemAvailability(editItem.itemId);
        }
      } else {
        const created = await createItem(payload);
        if (!shouldPublish && created?.itemId) {
          await toggleItemAvailability(created.itemId);
        }
      }

      // Plantilla propia de la organización (fase 3): se guarda después del
      // item; si falla, el combo ya quedó creado y se avisa en el toast.
      let toastKey = isEdit ? 'comboWizard.toast.updated' : 'comboWizard.toast.created';
      if (saveAsTemplate && templateName.trim()) {
        try {
          await createOrgTemplate({
            kind: ORG_TEMPLATE_KINDS.COMBO,
            name: templateName.trim(),
            description: presentation.description.trim().slice(0, 240),
            payload: buildComboTemplatePayload({
              slots,
              byId,
              templateId,
              discountPct,
              presentation,
              t,
            }),
          });
          toastKey = isEdit
            ? 'comboWizard.toast.updatedWithTemplate'
            : 'comboWizard.toast.createdWithTemplate';
        } catch (error) {
          console.error('[ComboWizard] save org template failed:', error);
          toastKey = 'comboWizard.toast.templateFailed';
        }
      }

      navigate('/menu', {
        state: {
          toast: t(toastKey, { name: presentation.name.trim() }),
        },
      });
    } catch (error) {
      console.error('[ComboWizard] save failed:', error);
      setSaveError(error.response?.data?.error || t('comboWizard.errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Render helpers ────────────────────────────────────────────────────
  const slotErrorFor = (slotId) => slotErrors.find((err) => err.slotId === slotId);

  const renderStepper = () => (
    <ol className="combo-wizard__steps" aria-label={t('comboWizard.stepsLabel')}>
      {STEP_KEYS.map((key, index) => {
        const number = index + 1;
        const state = number < step ? 'done' : number === step ? 'active' : 'pending';
        const disabled = isEdit && number === 1;
        return (
          <li
            key={key}
            className={`combo-wizard__step combo-wizard__step--${state}`}
            aria-current={state === 'active' ? 'step' : undefined}
          >
            <button
              type="button"
              className="combo-wizard__step-btn"
              disabled={disabled || number > step}
              onClick={() => setStep(number)}
            >
              <span className="combo-wizard__step-num">
                {state === 'done' ? <Check size={14} aria-hidden="true" /> : number}
              </span>
              <span className="combo-wizard__step-label">{t(`comboWizard.steps.${key}`)}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  const renderTemplates = () => (
    <section className="combo-wizard__panel" aria-labelledby="combo-wizard-step1">
      <h2 id="combo-wizard-step1">{t('comboWizard.step1.title')}</h2>
      <p className="combo-wizard__hint">{t('comboWizard.step1.hint')}</p>
      <div className="combo-templates">
        {COMBO_TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            className={`combo-template-card ${tpl.id === CUSTOM_TEMPLATE_ID ? 'combo-template-card--custom' : ''}`}
            onClick={() => handleSelectTemplate(tpl.id)}
          >
            <span className="combo-template-card__icon">
              <TemplateIcon name={tpl.icon} />
            </span>
            <span className="combo-template-card__name">
              {t(`comboWizard.templates.${tpl.id}.name`)}
            </span>
            <span className="combo-template-card__desc">
              {t(`comboWizard.templates.${tpl.id}.description`)}
            </span>
            <span className="combo-template-card__summary">{templateSummary(tpl, t)}</span>
            {tpl.defaultDiscountPct > 0 && (
              <span className="combo-template-card__discount">
                {t('comboWizard.step1.suggestedDiscount', { pct: tpl.defaultDiscountPct })}
              </span>
            )}
          </button>
        ))}
      </div>

      <TemplatePicker
        title={t('templates.own.title')}
        hint={t('templates.own.comboHint')}
        helpTopicKey="menu.orgTemplates"
        templates={[]}
        ownTemplates={orgTemplates.map((tpl) => ({
          id: tpl.templateId,
          name: tpl.name,
          description: tpl.description,
          chips: describeOrgTemplate(tpl, t),
        }))}
        onSelect={handleSelectOrgTemplate}
        onSelectOwn={handleSelectOrgTemplate}
        onDeleteOwn={handleDeleteOrgTemplate}
        ownLoading={orgLoading}
      />
    </section>
  );

  const renderSlot = (slot, index) => {
    const label = slotLabel(slot, t);
    const items = slotItems(slot);
    const selected = slot.itemIds.map((id) => byId.get(id)).filter(Boolean);
    const error = slotErrorFor(slot.slotId);
    const isQuick = quickCreate?.slotId === slot.slotId;

    return (
      <div
        key={slot.slotId}
        className={`combo-slot ${error ? 'combo-slot--error' : ''} ${selected.length ? 'combo-slot--filled' : ''}`}
      >
        <div className="combo-slot__head">
          <div className="combo-slot__title">
            <span className="combo-slot__index">{index + 1}</span>
            <h3>{label}</h3>
            <span className={`combo-slot__kind combo-slot__kind--${slot.kind}`}>
              {t(`comboWizard.kinds.${slot.kind}`)}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm combo-slot__remove"
            onClick={() => removeSlot(slot.slotId)}
            aria-label={t('comboWizard.step2.removeSlot', { label })}
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="combo-slot__controls">
          <label className="combo-slot__control">
            <span>{t('comboWizard.step2.filterCategory')}</span>
            <select
              value={slot.categoryId}
              onChange={(e) => updateSlot(slot.slotId, { categoryId: e.target.value })}
            >
              <option value="">{t('comboWizard.step2.allCategories')}</option>
              {pickableCategories.map((cat) => (
                <option key={cat.categoryId} value={cat.categoryId}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
          <label className="combo-slot__control combo-slot__control--search">
            <span>{t('comboWizard.step2.searchItems')}</span>
            <span className="combo-slot__search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                value={slotSearch[slot.slotId] || ''}
                onChange={(e) =>
                  setSlotSearch((prev) => ({ ...prev, [slot.slotId]: e.target.value }))
                }
                placeholder={t('comboWizard.step2.searchPlaceholder')}
              />
            </span>
          </label>
          {slot.kind === 'fixed' ? (
            <label className="combo-slot__control combo-slot__control--num">
              <span>{t('comboWizard.step2.quantity')}</span>
              <input
                type="number"
                min="1"
                value={slot.quantity}
                onChange={(e) =>
                  updateSlot(slot.slotId, { quantity: parseInt(e.target.value, 10) || 1 })
                }
              />
            </label>
          ) : (
            <div className="combo-slot__control combo-slot__control--range">
              <span>{t('comboWizard.step2.customerChooses')}</span>
              <div className="combo-slot__range">
                <label>
                  <span>{t('comboWizard.step2.min')}</span>
                  <input
                    type="number"
                    min="0"
                    value={slot.min}
                    onChange={(e) =>
                      updateSlot(slot.slotId, {
                        min: Math.max(0, parseInt(e.target.value, 10) || 0),
                      })
                    }
                  />
                </label>
                <label>
                  <span>{t('comboWizard.step2.max')}</span>
                  <input
                    type="number"
                    min="1"
                    value={slot.max}
                    onChange={(e) =>
                      updateSlot(slot.slotId, {
                        max: Math.max(1, parseInt(e.target.value, 10) || 1),
                      })
                    }
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        <ul
          className="combo-slot__items"
          role="listbox"
          aria-multiselectable={slot.kind === 'choice'}
        >
          {items.length === 0 && (
            <li className="combo-slot__empty">{t('comboWizard.step2.noItems')}</li>
          )}
          {items.map((item) => {
            const isSelected = slot.itemIds.includes(item.itemId);
            return (
              <li key={item.itemId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`combo-slot__item ${isSelected ? 'combo-slot__item--selected' : ''}`}
                  onClick={() => toggleSlotItem(slot, item)}
                >
                  <span className="combo-slot__item-check">
                    {isSelected && <Check size={14} aria-hidden="true" />}
                  </span>
                  <span className="combo-slot__item-name">{item.name}</span>
                  {!slot.categoryId && (
                    <span className="combo-slot__item-cat">{item.categoryName}</span>
                  )}
                  <span className="combo-slot__item-price">{formatCurrency(item.price)}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {slot.kind === 'choice' && selected.length > 0 && (
          <div className="combo-slot__extras">
            <span className="combo-slot__extras-title">
              {t('comboWizard.step2.extraPriceTitle')}
            </span>
            <div className="combo-slot__extras-grid">
              {selected.map((item) => (
                <label key={item.itemId} className="combo-slot__extra">
                  <span>{item.name}</span>
                  <span className="combo-slot__extra-input">
                    <span aria-hidden="true">+</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={slot.extraPrices[item.itemId] ?? 0}
                      onChange={(e) =>
                        updateSlot(slot.slotId, {
                          extraPrices: {
                            ...slot.extraPrices,
                            [item.itemId]: Math.max(0, parseFloat(e.target.value) || 0),
                          },
                        })
                      }
                      aria-label={t('comboWizard.step2.extraPriceFor', { name: item.name })}
                    />
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="combo-slot__foot">
          {!isQuick ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                setQuickCreate({ slotId: slot.slotId, name: '', price: '', error: '' })
              }
              disabled={!slot.categoryId}
              title={!slot.categoryId ? t('comboWizard.step2.quickCreateNeedsCategory') : undefined}
            >
              <Plus size={14} aria-hidden="true" /> {t('comboWizard.step2.quickCreate')}
            </button>
          ) : (
            <div className="combo-quick-create">
              <input
                type="text"
                placeholder={t('comboWizard.step2.quickCreateName')}
                value={quickCreate.name}
                onChange={(e) => setQuickCreate({ ...quickCreate, name: e.target.value })}
                aria-label={t('comboWizard.step2.quickCreateName')}
              />
              <input
                type="number"
                min="0"
                placeholder={t('comboWizard.step2.quickCreatePrice')}
                value={quickCreate.price}
                onChange={(e) => setQuickCreate({ ...quickCreate, price: e.target.value })}
                aria-label={t('comboWizard.step2.quickCreatePrice')}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => handleQuickCreate(slot)}
                disabled={quickCreate.saving}
              >
                {quickCreate.saving
                  ? t('common.loading')
                  : t('comboWizard.step2.quickCreateConfirm')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setQuickCreate(null)}
                disabled={quickCreate.saving}
              >
                {t('common.cancel')}
              </button>
              {quickCreate.error && (
                <span className="combo-quick-create__error" role="alert">
                  {quickCreate.error}
                </span>
              )}
            </div>
          )}
          {error && (
            <span className="combo-slot__error" role="alert">
              {t(`comboWizard.errors.slot.${error.reason}`)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderSlots = () => (
    <section className="combo-wizard__panel" aria-labelledby="combo-wizard-step2">
      <div className="combo-wizard__panel-head">
        <div>
          <h2 id="combo-wizard-step2">{t('comboWizard.step2.title')}</h2>
          <p className="combo-wizard__hint">
            {template && templateId !== CUSTOM_TEMPLATE_ID
              ? t('comboWizard.step2.hintTemplate', {
                  template: t(`comboWizard.templates.${templateId}.name`),
                })
              : t('comboWizard.step2.hintCustom')}
          </p>
        </div>
        {!isEdit && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>
            {t('comboWizard.step2.changeTemplate')}
          </button>
        )}
      </div>

      {pickableCategories.length === 0 && (
        <div className="combo-wizard__notice" role="status">
          <Info size={18} aria-hidden="true" />
          <span>
            {t('comboWizard.step2.noMenuItems')}{' '}
            <Link to="/menu/items/new">{t('menu.createItem')}</Link>
          </span>
        </div>
      )}

      <div className="combo-slots">{slots.map((slot, index) => renderSlot(slot, index))}</div>

      {slotErrorFor('__none__') && (
        <p className="combo-slot__error" role="alert">
          {t('comboWizard.errors.slot.noSlots')}
        </p>
      )}

      <div className="combo-wizard__add-slot">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => addSlot('fixed')}>
          <Plus size={14} aria-hidden="true" /> {t('comboWizard.step2.addFixedSlot')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => addSlot('choice')}
        >
          <Plus size={14} aria-hidden="true" /> {t('comboWizard.step2.addChoiceSlot')}
        </button>
      </div>
    </section>
  );

  const renderPricing = () => {
    const combosCategory = findCombosCategory(categories);
    return (
      <section className="combo-wizard__panel" aria-labelledby="combo-wizard-step3">
        <h2 id="combo-wizard-step3">{t('comboWizard.step3.title')}</h2>
        <div className="combo-pricing">
          <div className="combo-pricing__form">
            <div className="form-group">
              <label htmlFor="combo-name">
                {t('comboWizard.step3.name')} <span className="required">*</span>
              </label>
              <input
                id="combo-name"
                type="text"
                value={presentation.name}
                onChange={(e) => setPresentation({ ...presentation, name: e.target.value })}
                maxLength={80}
              />
              {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="combo-description">{t('comboWizard.step3.description')}</label>
              <textarea
                id="combo-description"
                rows={3}
                value={presentation.description}
                onChange={(e) => {
                  setDescriptionDirty(true);
                  setPresentation({ ...presentation, description: e.target.value });
                }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm combo-pricing__regen"
                onClick={() => {
                  setDescriptionDirty(false);
                  setPresentation({ ...presentation, description: buildDescription() });
                }}
              >
                {t('comboWizard.step3.generateDescription')}
              </button>
              <span className="help-text">
                {descriptionDirty
                  ? t('comboWizard.step3.descriptionManualHint')
                  : t('comboWizard.step3.descriptionAutoHint')}
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="combo-badge">{t('comboWizard.step3.badge')}</label>
              <input
                id="combo-badge"
                type="text"
                value={presentation.badge}
                maxLength={24}
                placeholder={t('comboWizard.step3.badgePlaceholder')}
                onChange={(e) => setPresentation({ ...presentation, badge: e.target.value })}
              />
              <span className="help-text">{t('comboWizard.step3.badgeHint')}</span>
            </div>

            <div className="form-group">
              <label htmlFor="combo-category">
                {t('comboWizard.step3.targetCategory')} <span className="required">*</span>
              </label>
              <select
                id="combo-category"
                value={presentation.categoryId}
                onChange={(e) => setPresentation({ ...presentation, categoryId: e.target.value })}
              >
                <option value="">{t('menu.selectCategory')}</option>
                {!combosCategory && (
                  <option value={NEW_COMBOS_CATEGORY}>
                    {t('comboWizard.step3.createCombosCategory')}
                  </option>
                )}
                {categories.map((cat) => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {fieldErrors.categoryId && (
                <span className="field-error">{fieldErrors.categoryId}</span>
              )}
            </div>

            <div className="form-group">
              <span className="combo-pricing__label">{t('comboWizard.step3.image')}</span>
              <ImageUpload
                resourceType="items"
                resourceId={editItem?.itemId || `combo-${templateId || 'custom'}`}
                currentImageUrl={presentation.imageUrl}
                onImageUploaded={(url) => setPresentation({ ...presentation, imageUrl: url })}
              />
              {mainImageCandidate && presentation.imageUrl !== mainImageCandidate.imageUrl && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() =>
                    setPresentation({ ...presentation, imageUrl: mainImageCandidate.imageUrl })
                  }
                >
                  {t('comboWizard.step3.useImageOf', { name: mainImageCandidate.name })}
                </button>
              )}
            </div>

            <div className="form-group">
              <span className="combo-pricing__label">{t('comboWizard.step3.availability')}</span>
              <AvailabilitySchedule
                value={presentation.availability}
                onChange={(availability) => setPresentation({ ...presentation, availability })}
                label=""
              />
            </div>
          </div>

          <aside className="combo-pricing__box" aria-label={t('comboWizard.step3.priceBoxLabel')}>
            <div className="combo-pricing__row">
              <span>{t('comboWizard.step3.componentsSum')}</span>
              <strong>{formatCurrency(componentsSum)}</strong>
            </div>
            <p className="combo-pricing__note">{t('comboWizard.step3.componentsSumHint')}</p>

            <div
              className="combo-pricing__presets"
              role="group"
              aria-label={t('comboWizard.step3.discount')}
            >
              {DISCOUNT_PRESETS.map((pct) => (
                <button
                  key={pct}
                  type="button"
                  className={`combo-pricing__preset ${pricing.mode === 'pct' && pricing.pct === pct ? 'active' : ''}`}
                  onClick={() => setPricing({ mode: 'pct', pct, price: '' })}
                  aria-pressed={pricing.mode === 'pct' && pricing.pct === pct}
                >
                  −{pct}%
                </button>
              ))}
              <button
                type="button"
                className={`combo-pricing__preset ${pricing.mode === 'manual' ? 'active' : ''}`}
                onClick={() => {
                  setPricing({ mode: 'manual', pct: 0, price: String(effectivePrice || '') });
                  // RES-68: el campo queda pre-cargado con el valor sugerido. Sin esto,
                  // escribir ahí inserta en vez de reemplazar (ej. "3000" → "30002700").
                  // El setTimeout espera al re-render que hace editable el input.
                  setTimeout(() => {
                    priceInputRef.current?.focus();
                    priceInputRef.current?.select();
                  }, 0);
                }}
                aria-pressed={pricing.mode === 'manual'}
              >
                {t('comboWizard.step3.manual')}
              </button>
            </div>

            <div className="form-group combo-pricing__price">
              <label htmlFor="combo-price">
                {t('comboWizard.step3.price')} <span className="required">*</span>
              </label>
              <input
                id="combo-price"
                ref={priceInputRef}
                type="number"
                min="0"
                step="1"
                value={pricing.mode === 'manual' ? pricing.price : effectivePrice}
                onChange={(e) => setPricing({ mode: 'manual', pct: 0, price: e.target.value })}
              />
              {fieldErrors.price && <span className="field-error">{fieldErrors.price}</span>}
            </div>

            <div className="combo-pricing__result">
              {discountPct > 0 ? (
                <span>
                  {t('comboWizard.step3.savings', {
                    amount: formatCurrency(componentsSum - effectivePrice),
                    pct: discountPct,
                  })}
                </span>
              ) : (
                <span className="combo-pricing__warn">{t('comboWizard.step3.noSavings')}</span>
              )}
              {pricing.mode === 'pct' && componentsSum > 0 && (
                <span className="help-text combo-pricing__rounding">
                  {t('comboWizard.step3.roundingHint')}
                </span>
              )}
            </div>
          </aside>
        </div>
      </section>
    );
  };

  const renderReview = () => {
    const fixed = [];
    const choices = [];
    let newGroups = 0;
    let reusedGroups = 0;
    for (const slot of slots) {
      const items = slot.itemIds.map((id) => byId.get(id)).filter(Boolean);
      if (slot.kind === 'fixed') {
        fixed.push(...items.map((item) => ({ ...item, quantity: slot.quantity || 1 })));
      } else {
        choices.push({ slot, items });
        if (slot.groupId) reusedGroups += 1;
        else newGroups += 1;
      }
    }
    const categoryName = isNewCombosCategory(presentation.categoryId)
      ? t('comboWizard.defaults.combosCategory')
      : categories.find((cat) => cat.categoryId === presentation.categoryId)?.name || '';

    return (
      <section className="combo-wizard__panel" aria-labelledby="combo-wizard-step4">
        <h2 id="combo-wizard-step4">{t('comboWizard.step4.title')}</h2>
        <div className="combo-review">
          <div className="combo-preview" aria-label={t('comboWizard.step4.previewLabel')}>
            {presentation.imageUrl ? (
              <img src={presentation.imageUrl} alt="" className="combo-preview__image" />
            ) : (
              <div className="combo-preview__image combo-preview__image--empty">
                <TemplateIcon name={template?.icon} size={32} />
              </div>
            )}
            <div className="combo-preview__body">
              <div className="combo-preview__badges">
                <span className="combo-preview__badge combo-preview__badge--combo">
                  {t('comboWizard.step4.comboBadge')}
                </span>
                {presentation.badge &&
                  presentation.badge.trim().toLowerCase() !==
                    t('comboWizard.step4.comboBadge').toLowerCase() && (
                    <span className="combo-preview__badge combo-preview__badge--promo">
                      {presentation.badge}
                    </span>
                  )}
              </div>
              <h3>{presentation.name}</h3>
              {presentation.description && <p>{presentation.description}</p>}
              <div className="combo-preview__price">
                <strong>{formatCurrency(effectivePrice)}</strong>
                {componentsSum > effectivePrice && <s>{formatCurrency(componentsSum)}</s>}
              </div>
              <ul className="combo-preview__list">
                {fixed.map((item) => (
                  <li key={item.itemId}>
                    {t('comboWizard.step4.includes')}{' '}
                    {item.quantity > 1 ? `${item.quantity}× ` : ''}
                    {item.name}
                  </li>
                ))}
                {choices.map(({ slot, items }) => (
                  <li key={slot.slotId}>
                    {t('comboWizard.step4.chooses', {
                      count: slot.max || 1,
                      label: slotLabel(slot, t).toLowerCase(),
                    })}
                    : {items.map((item) => item.name).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="combo-summary">
            <h3>{t('comboWizard.step4.willCreate')}</h3>
            <ul>
              <li>
                {isEdit ? t('comboWizard.step4.itemUpdate') : t('comboWizard.step4.itemCreate')}{' '}
                <strong>{categoryName}</strong>
              </li>
              {newGroups > 0 && <li>{t('comboWizard.step4.groupsNew', { count: newGroups })}</li>}
              {reusedGroups > 0 && (
                <li>{t('comboWizard.step4.groupsReuse', { count: reusedGroups })}</li>
              )}
              {isNewCombosCategory(presentation.categoryId) && (
                <li>{t('comboWizard.step4.categoryNew')}</li>
              )}
            </ul>
            <p className="combo-wizard__hint">{t('comboWizard.step4.groupsHint')}</p>

            {isEdit && (
              <label className="combo-summary__publish">
                <input
                  type="checkbox"
                  checked={publish}
                  onChange={(e) => setPublish(e.target.checked)}
                />
                {t('comboWizard.step4.publishedToggle')}
              </label>
            )}

            <div className="combo-summary__template">
              <label className="combo-summary__publish">
                <input
                  type="checkbox"
                  checked={saveAsTemplate}
                  onChange={(e) => {
                    setSaveAsTemplate(e.target.checked);
                    if (e.target.checked && !templateName) {
                      setTemplateName(presentation.name.trim());
                    }
                  }}
                />
                {t('comboWizard.step4.saveAsTemplate')}
              </label>
              {saveAsTemplate && (
                <>
                  <input
                    type="text"
                    className="combo-summary__template-name"
                    value={templateName}
                    maxLength={80}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={t('comboWizard.step4.templateNamePlaceholder')}
                    aria-label={t('comboWizard.step4.templateName')}
                  />
                  <p className="combo-wizard__hint">{t('comboWizard.step4.saveAsTemplateHint')}</p>
                </>
              )}
            </div>

            {saveError && (
              <div className="combo-wizard__error" role="alert">
                {saveError}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const renderFooter = () => (
    <div className="combo-wizard__footer">
      <div>
        {step > 1 && !(isEdit && step === 2) && (
          <button
            type="button"
            className="btn btn-ghost combo-wizard__btn"
            onClick={() => setStep(step - 1)}
            disabled={saving}
          >
            <ChevronLeft size={16} aria-hidden="true" /> {t('common.back')}
          </button>
        )}
      </div>
      <div className="combo-wizard__footer-actions">
        {step === 2 && (
          <button type="button" className="btn btn-primary combo-wizard__btn" onClick={goToPricing}>
            {t('comboWizard.actions.next')} <ChevronRight size={16} aria-hidden="true" />
          </button>
        )}
        {step === 3 && (
          <button type="button" className="btn btn-primary combo-wizard__btn" onClick={goToReview}>
            {t('comboWizard.actions.review')} <ChevronRight size={16} aria-hidden="true" />
          </button>
        )}
        {step === 4 && !isEdit && (
          <>
            <button
              type="button"
              className="btn btn-secondary combo-wizard__btn"
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              {t('comboWizard.actions.saveDraft')}
            </button>
            <button
              type="button"
              className="btn btn-primary combo-wizard__btn"
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              {saving ? t('common.loading') : t('comboWizard.actions.publish')}
            </button>
          </>
        )}
        {step === 4 && isEdit && (
          <button
            type="button"
            className="btn btn-primary combo-wizard__btn"
            onClick={() => handleSave(publish)}
            disabled={saving}
          >
            {saving ? t('common.loading') : t('comboWizard.actions.saveChanges')}
          </button>
        )}
      </div>
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="combo-wizard">
        <p className="combo-wizard__hint">{t('common.loading')}</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="combo-wizard">
        <div className="combo-wizard__error" role="alert">
          {loadError}
        </div>
      </div>
    );
  }

  if (legacyCombo && editItem) {
    return (
      <div className="combo-wizard">
        <div className="combo-wizard__notice" role="status">
          <Info size={18} aria-hidden="true" />
          <span>{t('comboWizard.edit.legacyNotice')}</span>
        </div>
        <Link to={`/menu/items/edit/${editItem.itemId}`} className="btn btn-primary">
          {t('comboWizard.edit.openFullForm')}
        </Link>
      </div>
    );
  }

  return (
    <div className="combo-wizard">
      <header className="combo-wizard__header">
        <div>
          <h1>
            {isEdit
              ? t('comboWizard.edit.title', { name: editItem?.name || '' })
              : t('comboWizard.title')}
            <HelpIcon topicKey="menu.comboWizard" />
          </h1>
          <p>{t('comboWizard.subtitle')}</p>
        </div>
        {isEdit && editItem && (
          <Link to={`/menu/items/edit/${editItem.itemId}`} className="btn btn-ghost btn-sm">
            {t('comboWizard.edit.openFullForm')}
          </Link>
        )}
      </header>

      {renderStepper()}

      {step === 1 && renderTemplates()}
      {step === 2 && renderSlots()}
      {step === 3 && renderPricing()}
      {step === 4 && renderReview()}

      {step > 1 && renderFooter()}
    </div>
  );
}

export default ComboWizardPage;
